import { Helmet } from 'react-helmet-async';

interface PerformanceOptimizationProps {
  preloadImages?: string[];
  prefetchRoutes?: string[];
  criticalResources?: string[];
}

const PerformanceOptimization = ({ 
  preloadImages = [], 
  prefetchRoutes = [],
  criticalResources = []
}: PerformanceOptimizationProps) => {
  
  // Default critical resources for SeniorThrive
  const defaultCriticalResources = [
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
    'https://www.googletagmanager.com/gtag/js'
  ];

  // Default routes to prefetch for better navigation
  const defaultPrefetchRoutes = [
    '/older-adults',
    '/thrivefamily', 
    '/caregivers',
    '/pricing',
    '/features'
  ];

  const allCriticalResources = [...defaultCriticalResources, ...criticalResources];
  const allPrefetchRoutes = [...defaultPrefetchRoutes, ...prefetchRoutes];

  return (
    <Helmet>
      {/* DNS Prefetch for external domains */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      <link rel="dns-prefetch" href="//cdn.jotfor.ms" />

      {/* Preconnect to critical origins */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />

      {/* Preload critical resources */}
      {allCriticalResources.map((resource, index) => (
        <link
          key={`critical-${index}`}
          rel="preload"
          href={resource}
          as={resource.includes('.css') ? 'style' : 'script'}
          crossOrigin={resource.includes('fonts') ? 'anonymous' : undefined}
        />
      ))}

      {/* Preload critical images */}
      {preloadImages.map((image, index) => (
        <link
          key={`image-${index}`}
          rel="preload"
          href={image}
          as="image"
          imageSrcSet={`${image}?w=480 480w, ${image}?w=768 768w, ${image}?w=1024 1024w`}
          imageSizes="(max-width: 768px) 100vw, 50vw"
        />
      ))}

      {/* Prefetch routes for better navigation performance */}
      {allPrefetchRoutes.map((route, index) => (
        <link
          key={`prefetch-${index}`}
          rel="prefetch"
          href={route}
        />
      ))}

      {/* Resource hints for better performance */}
      <link rel="modulepreload" href="/src/main.tsx" />
      
      {/* Performance and Core Web Vitals optimizations */}
      <meta httpEquiv="Content-Security-Policy" content="upgrade-insecure-requests" />
      
      {/* Optimize for Core Web Vitals */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "SeniorThrive",
          "url": "https://seniorthrive.com",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://seniorthrive.com/support?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
      </script>

      {/* Inline critical JavaScript for performance */}
      <script dangerouslySetInnerHTML={{
        __html: `
          // Critical performance monitoring
          (function() {
            // Mark navigation start
            if ('performance' in window && 'mark' in performance) {
              performance.mark('navigation-start');
            }
            
            // Optimize image loading
            if ('loading' in HTMLImageElement.prototype) {
              document.addEventListener('DOMContentLoaded', function() {
                const images = document.querySelectorAll('img[data-src]');
                images.forEach(img => {
                  img.src = img.dataset.src;
                  img.removeAttribute('data-src');
                });
              });
            }
            
            // Prefetch resources on idle
            if ('requestIdleCallback' in window) {
              requestIdleCallback(function() {
                const link = document.createElement('link');
                link.rel = 'prefetch';
                link.href = '/blog';
                document.head.appendChild(link);
              });
            }
          })();
        `
      }} />
    </Helmet>
  );
};

export default PerformanceOptimization;